import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaLaptopCode, FaHeartbeat, FaCoffee, FaPlane, FaChalkboardTeacher, FaUsers, FaArrowRight, FaMapMarkerAlt, FaClock } from 'react-icons/fa';
import heroImage from '../assets/d19a8f50f70944b0c4b2fdf4553d2462.jpg';

const CareersPage = () => {
    const benefits = [
        { icon: FaLaptopCode, title: "Latest Tech", desc: "Work with cutting-edge technologies and tools." },
        { icon: FaHeartbeat, title: "Health & Wellness", desc: "Comprehensive health insurance and wellness programs." },
        { icon: FaCoffee, title: "Work-Life Balance", desc: "Flexible working hours and remote options." },
        { icon: FaChalkboardTeacher, title: "Learning & Growth", desc: "Annual budget for courses, conferences, and books." },
        { icon: FaUsers, title: "Inclusive Culture", desc: "A diverse team where every voice is heard and valued." },
        { icon: FaPlane, title: "Team Retreats", desc: "Annual company offsites to connect and recharge." }
    ];

    const jobs = [
        {
            id: 1,
            title: "Senior Full Stack Engineer",
            department: "Engineering",
            location: "Remote / Hybrid",
            type: "Full-time",
            tags: ["React", "Node.js", "AWS"]
        },
        {
            id: 2,
            title: "AI/ML Research Scientist",
            department: "R&D",
            location: "San Francisco, CA",
            type: "Full-time",
            tags: ["Python", "TensorFlow", "NLP"]
        },
        {
            id: 3,
            title: "Product Designer (UI/UX)",
            department: "Design",
            location: "Remote",
            type: "Full-time",
            tags: ["Figma", "Prototyping", "User Research"]
        },
        {
            id: 4,
            title: "Digital Marketing Manager",
            department: "Marketing",
            location: "London, UK",
            type: "Full-time",
            tags: ["SEO", "Content Strategy", "Analytics"]
        }
    ];

    return (
        <div className="bg-white font-sans text-slate-900">
            {/* Hero Section */}
            <section className="relative h-[50vh] min-h-[400px] overflow-hidden">
                <div
                    className="absolute inset-0 bg-cover bg-center"
                    style={{ backgroundImage: `url('${heroImage}')` }}
                >
                    <div className="absolute inset-0 bg-slate-900/70"></div>
                </div>

                <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-12 flex items-center pt-20">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="max-w-3xl"
                    >
                        <p className="text-cyan-400 font-bold tracking-widest uppercase mb-4">Careers at Tech Inovant</p>
                        <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
                            Build the <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500">Future</span> With Us
                        </h1>
                        <p className="text-xl text-white leading-relaxed font-light max-w-2xl">
                            Join a team of visionaries, engineers, and creators dedicated to solving the world's toughest challenges through technology.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Culture & Values */}
            <section className="py-24 bg-white">
                <div className="max-w-7xl mx-auto px-6 lg:px-12 text-center">
                    <div className="max-w-3xl mx-auto mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">More Than Just a Job</h2>
                        <p className="text-lg text-slate-600 leading-relaxed">
                            We believe that great work happens when you feel challenged, supported, and inspired. At Tech Inovant, we foster a culture of continuous learning, radical transparency, and collaborative innovation.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-8">
                        {benefits.map((benefit, idx) => (
                            <motion.div
                                key={idx}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: idx * 0.1 }}
                                className="p-8 rounded-2xl bg-slate-50 border border-slate-100 hover:shadow-xl transition-all duration-300 group"
                            >
                                <div className="w-14 h-14 mx-auto bg-blue-100 rounded-full flex items-center justify-center mb-6 group-hover:bg-blue-600 transition-colors duration-300">
                                    <benefit.icon className="text-2xl text-blue-600 group-hover:text-white transition-colors duration-300" />
                                </div>
                                <h3 className="text-xl font-bold text-slate-900 mb-3">{benefit.title}</h3>
                                <p className="text-slate-600">{benefit.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Open Roles */}
            <section id="openings" className="py-24 bg-slate-50">
                <div className="max-w-5xl mx-auto px-6 lg:px-12">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Open Positions</h2>
                        <p className="text-slate-600">Find your place in our growing team.</p>
                    </div>

                    <div className="space-y-6">
                        {jobs.map((job) => (
                            <motion.div
                                key={job.id}
                                initial={{ opacity: 0, x: -20 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                className="bg-white p-8 rounded-xl shadow-sm border border-slate-200 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 hover:border-blue-400 transition-colors"
                            >
                                <div>
                                    <h3 className="text-xl font-bold text-slate-900 mb-2">{job.title}</h3>
                                    <div className="flex flex-wrap gap-4 text-sm text-slate-500 mb-4">
                                        <span className="flex items-center gap-1"><FaUsers /> {job.department}</span>
                                        <span className="flex items-center gap-1"><FaMapMarkerAlt /> {job.location}</span>
                                        <span className="flex items-center gap-1"><FaClock /> {job.type}</span>
                                    </div>
                                    <div className="flex gap-2">
                                        {job.tags.map((tag, i) => (
                                            <span key={i} className="px-3 py-1 bg-slate-100 text-slate-600 text-xs rounded-full font-medium">
                                                {tag}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                                <button className="px-6 py-3 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap shadow-md">
                                    Apply Now
                                </button>
                            </motion.div>
                        ))}
                    </div>

                    <div className="mt-16 text-center">
                        <p className="text-slate-600 mb-6">Don't see the right role for you?</p>
                        <a href="mailto:careers@techinovant.com" className="inline-flex items-center text-blue-600 font-bold hover:underline">
                            Send us your resume <FaArrowRight className="ml-2" />
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default CareersPage;
